import type { FC, SVGProps } from "react"
import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import Fuse from "fuse.js"
import {
	IconLink2SlashOutline18 as Link2Off,
	IconUserXmarkOutline18 as UserX,
} from "nucleo-ui-outline-18"

import type { FeedbackOrigin, FeedbackState, PainLevel } from "@productlane/db"
import type { IconType } from "@productlane/ui"
import { FeedbackOriginTextRecord } from "@productlane/lib"
import {
	Avatar,
	IssueStatusIcon,
	PriorityIcons,
	PriorityNaming,
} from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { FilterType } from "./filter-popover-items/types"
import type { NumericFilter, NumericOperator } from "./threads-filter-state"
import { dedupeById } from "@/components/filters"
import { companyDomain } from "@/lib/companies"
import { FeedbackOriginIconRecord } from "@/lib/feedback"
import { getProjectIcon } from "@/lib/projects"
import { useSlackWorkspaceOptions } from "@/lib/slack-workspaces"
import { useCompanyOptions, useContactOptions } from "@/lib/use-entity-options"
import { useInboxAddresses } from "@/lib/use-inbox-addresses"
import { usePortalMarks } from "@/lib/use-portal-marks"
import { CompanyLogo } from "@/screens/app/customers/company-logo"
import { distinctIssueStatuses } from "./filter-popover-items/issue-status-filter-items"
import {
	REPLY_STATES,
	ReplyStateIcons,
	ReplyStateNaming,
} from "./filter-popover-items/reply-state-filter-items"
import {
	StatusIcons,
	StatusNaming,
} from "./filter-popover-items/status-filter-items"
import { FILTER_CATEGORIES } from "./threads-filter"
import { useThreadsFilterState } from "./threads-filter-state"

const NUMERIC_OPERATORS: { operator: NumericOperator; label: string }[] = [
	{ operator: ">=", label: "Greater than or equal to" },
	{ operator: "<=", label: "Less than or equal to" },
	{ operator: "=", label: "Equal to" },
	{ operator: "!=", label: "Not equal to" },
]

export interface SearchableFilterItem {
	id: string
	category: FilterType
	categoryLabel: string
	label: string
	secondaryLabel?: string
	searchText: string
	Icon?: IconType | LucideIcon
	iconColor?: string
	onSelect: () => void
	isSelected: boolean
	renderCustom?: () => React.ReactNode
}

const AVAILABLE_ORIGINS: FeedbackOrigin[] = [
	"API",
	"INAPP",
	"PORTAL",
	"SUPPORT_PORTAL",
	"EMAIL",
	"WIDGET",
	"ZAPIER",
	"HUBSPOT",
	"CALENDAR",
	"AI_CHAT",
	"LIVE_CHAT",
]

const PAIN_LEVELS: PainLevel[] = ["UNKNOWN", "LOW", "MEDIUM", "HIGH"]

const FEEDBACK_STATES: FeedbackState[] = [
	"NEW",
	"PROCESSED",
	"COMPLETED",
	"SNOOZED",
]

export function useSearchableFilterItems({
	onCategorySelect,
	enabled = true,
	query = "",
}: {
	onCategorySelect: (type: FilterType) => void
	enabled?: boolean
	/** What the user typed; people and companies are searched on the server with it. */
	query?: string
}) {
	const [users] = useQuery(queries.users())
	const [projects] = useQuery(queries.projects())
	const [tags] = useQuery(queries.tags())
	const [tagGroups] = useQuery(queries.tagGroups())
	const [issues] = useQuery(queries.issues(), { enabled })

	const filterState = useThreadsFilterState()
	const { contacts } = useContactOptions({
		query,
		selectedIds: filterState.personIds,
		enabled,
	})
	const { companies } = useCompanyOptions({
		query,
		selectedIds: filterState.companyIds,
		enabled,
	})

	const tagGroupColorMap = useMemo(() => {
		const colorMap = new Map<string, string>()
		tagGroups.forEach((group) => {
			colorMap.set(group.id, group.color)
		})
		return colorMap
	}, [tagGroups])

	const portalMarks = usePortalMarks()
	const inboxAddresses = useInboxAddresses()
	const slackWorkspaces = useSlackWorkspaceOptions()

	const allSearchableItems = useMemo((): SearchableFilterItem[] => {
		const items: SearchableFilterItem[] = []

		FILTER_CATEGORIES.forEach((cat) => {
			if (cat.type === "Portals" && portalMarks.length === 0) return
			if (cat.type === "SlackWorkspaces" && slackWorkspaces.length < 2) return
			if (cat.type === "SlackChannels" && slackWorkspaces.length === 0) return
			if (cat.type === "Inboxes" && inboxAddresses.length === 0) return
			items.push({
				id: `category-${cat.type}`,
				category: cat.type,
				categoryLabel: cat.label,
				label: cat.label,
				searchText: cat.label,
				Icon: cat.Icon,
				onSelect: () => onCategorySelect(cat.type),
				isSelected: false,
			})
		})

		items.push({
			id: "assignee-unassigned",
			category: "Assignees",
			categoryLabel: "Assignee",
			label: "Unassigned",
			searchText: "Assignee Unassigned",
			Icon: UserX,
			onSelect: () => {
				if (filterState.assigneeIds.includes(null)) {
					filterState.removeAssignee(null)
				} else {
					filterState.addAssignee(null)
				}
			},
			isSelected: filterState.assigneeIds.includes(null),
		})

		users.forEach((user) => {
			items.push({
				id: `assignee-${user.id}`,
				category: "Assignees",
				categoryLabel: "Assignee",
				label: user.name ?? "",
				searchText: `Assignee ${user.name ?? ""}`,
				onSelect: () => {
					if (filterState.assigneeIds.includes(user.id)) {
						filterState.removeAssignee(user.id)
					} else {
						filterState.addAssignee(user.id)
					}
				},
				isSelected: filterState.assigneeIds.includes(user.id),
				renderCustom: () => (
					<Avatar image={user.imageUrl} name={user.name ?? ""} size="xs" />
				),
			})
		})

		users.forEach((user) => {
			items.push({
				id: `creator-${user.id}`,
				category: "Creators",
				categoryLabel: "Creator",
				label: user.name ?? "",
				searchText: `Creator ${user.name ?? ""}`,
				onSelect: () => {
					if (filterState.creatorIds.includes(user.id)) {
						filterState.removeCreator(user.id)
					} else {
						filterState.addCreator(user.id)
					}
				},
				isSelected: filterState.creatorIds.includes(user.id),
			})
		})

		AVAILABLE_ORIGINS.forEach((origin) => {
			const originName = FeedbackOriginTextRecord[origin].replace("via ", "")
			items.push({
				id: `origin-${origin}`,
				category: "Origins",
				categoryLabel: "Origin",
				label: originName,
				searchText: `Origin ${originName}`,
				Icon: FeedbackOriginIconRecord[origin],
				onSelect: () => {
					if (filterState.origins.includes(origin)) {
						filterState.removeOrigin(origin)
					} else {
						filterState.addOrigin(origin)
					}
				},
				isSelected: filterState.origins.includes(origin),
			})
		})

		portalMarks.forEach((mark) => {
			const isSelected = filterState.portalInstanceIds.includes(mark.id)
			items.push({
				id: `portal-${mark.id ?? "main"}`,
				category: "Portals",
				categoryLabel: "Portal",
				label: mark.name,
				searchText: `Portal ${mark.name}`,
				renderCustom: () => (
					<Avatar
						name={mark.name}
						image={mark.logoUrl}
						colorSeed={mark.colorSeed}
						roundness="sm"
						size="xs"
						className="flex-none"
					/>
				),
				onSelect: () => {
					if (isSelected) {
						filterState.removePortalInstance(mark.id)
					} else {
						filterState.addPortalInstance(mark.id)
					}
				},
				isSelected,
			})
		})

		// Channels can run into the thousands, so only the Slack channel category searches them.
		if (slackWorkspaces.length > 1) {
			slackWorkspaces.forEach((workspace) => {
				const isSelected = filterState.slackWorkspaceIds.includes(workspace.id)
				items.push({
					id: `slack-workspace-${workspace.id}`,
					category: "SlackWorkspaces",
					categoryLabel: "Slack workspace",
					label: workspace.name,
					searchText: `Slack ${workspace.name}`,
					renderCustom: () => (
						<Avatar
							name={workspace.name}
							image={workspace.iconUrl ?? undefined}
							roundness="sm"
							size="xs"
							className="flex-none"
						/>
					),
					onSelect: () => {
						if (isSelected) {
							filterState.removeSlackWorkspace(workspace.id)
						} else {
							filterState.addSlackWorkspace(workspace.id)
						}
					},
					isSelected,
				})
			})
		}

		inboxAddresses.forEach((address) => {
			const isSelected = filterState.emailAddressIds.includes(address.id)
			items.push({
				id: `inbox-${address.id}`,
				category: "Inboxes",
				categoryLabel: "Inbox",
				label: address.primaryEmail,
				searchText: `Inbox ${address.primaryEmail}`,
				onSelect: () => {
					if (isSelected) {
						filterState.removeEmailAddress(address.id)
					} else {
						filterState.addEmailAddress(address.id)
					}
				},
				isSelected,
			})
		})

		items.push({
			id: "origin-slack",
			category: "Origins",
			categoryLabel: "Origin",
			label: "Slack Connect",
			searchText: "Origin Slack Connect",
			Icon: FeedbackOriginIconRecord.SLACK_CONNECT,
			onSelect: () => {
				if (
					filterState.origins.includes("SLACK_CONNECT") ||
					filterState.origins.includes("SLACK")
				) {
					filterState.removeOrigin("SLACK_CONNECT")
					filterState.removeOrigin("SLACK")
				} else {
					filterState.addOrigin("SLACK_CONNECT")
					filterState.addOrigin("SLACK")
				}
			},
			isSelected:
				filterState.origins.includes("SLACK_CONNECT") ||
				filterState.origins.includes("SLACK"),
		})

		items.push({
			id: "origin-intercom",
			category: "Origins",
			categoryLabel: "Origin",
			label: "Intercom",
			searchText: "Origin Intercom",
			Icon: FeedbackOriginIconRecord.INTERCOM,
			onSelect: () => {
				if (
					filterState.origins.includes("INTERCOM") ||
					filterState.origins.includes("INTERCOM_ATTACHMENT")
				) {
					filterState.removeOrigin("INTERCOM")
					filterState.removeOrigin("INTERCOM_ATTACHMENT")
				} else {
					filterState.addOrigin("INTERCOM")
					filterState.addOrigin("INTERCOM_ATTACHMENT")
				}
			},
			isSelected:
				filterState.origins.includes("INTERCOM") ||
				filterState.origins.includes("INTERCOM_ATTACHMENT"),
		})

		PAIN_LEVELS.forEach((level) => {
			items.push({
				id: `priority-${level}`,
				category: "Priorities",
				categoryLabel: "Priority",
				label: PriorityNaming[level],
				searchText: `Priority ${PriorityNaming[level]}`,
				Icon: PriorityIcons[level] as IconType,
				onSelect: () => {
					if (filterState.painLevels.includes(level)) {
						filterState.removePainLevel(level)
					} else {
						filterState.addPainLevel(level)
					}
				},
				isSelected: filterState.painLevels.includes(level),
			})
		})

		FEEDBACK_STATES.forEach((status) => {
			items.push({
				id: `status-${status}`,
				category: "Statuses",
				categoryLabel: "Status",
				label: StatusNaming[status],
				searchText: `Status ${StatusNaming[status]}`,
				Icon: StatusIcons[status],
				onSelect: () => {
					if (filterState.statuses.includes(status)) {
						filterState.removeStatus(status)
					} else {
						filterState.addStatus(status)
					}
				},
				isSelected: filterState.statuses.includes(status),
			})
		})

		REPLY_STATES.forEach((replyState) => {
			items.push({
				id: `reply-state-${replyState}`,
				category: "ReplyStates",
				categoryLabel: "Response",
				label: ReplyStateNaming[replyState],
				searchText: `Response ${ReplyStateNaming[replyState]}`,
				Icon: ReplyStateIcons[replyState],
				onSelect: () => {
					if (filterState.replyStates.includes(replyState)) {
						filterState.removeReplyState(replyState)
					} else {
						filterState.addReplyState(replyState)
					}
				},
				isSelected: filterState.replyStates.includes(replyState),
			})
		})

		projects.forEach((project) => {
			items.push({
				id: `project-${project.id}`,
				category: "Projects",
				categoryLabel: "Project",
				label: project.name,
				searchText: `Project ${project.name} ${project.publicTitle ?? ""} ${project.description ?? ""}`,
				Icon: getProjectIcon(project.icon),
				iconColor: project.color ?? undefined,
				onSelect: () => {
					if (filterState.projectIds.includes(project.id)) {
						filterState.removeProject(project.id)
					} else {
						filterState.addProject(project.id)
					}
				},
				isSelected: filterState.projectIds.includes(project.id),
			})
		})

		tags.forEach((tag) => {
			items.push({
				id: `tag-${tag.id}`,
				category: "Tags",
				categoryLabel: "Tag",
				label: tag.name,
				searchText: `Tag ${tag.name}`,
				Icon: getProjectIcon(tag.icon),
				iconColor: tagGroupColorMap.get(tag.tagGroupId),
				onSelect: () => {
					if (filterState.tagIds.includes(tag.id)) {
						filterState.removeTag(tag.id)
					} else {
						filterState.addTag(tag.id)
					}
				},
				isSelected: filterState.tagIds.includes(tag.id),
			})
		})

		issues.forEach((issue) => {
			items.push({
				id: `issue-${issue.id}`,
				category: "Issues",
				categoryLabel: "Issue",
				label: `${issue.identifier}: ${issue.title}`,
				searchText: `Issue ${issue.identifier} ${issue.title} ${issue.publicTitle ?? ""} ${issue.description ?? ""}`,
				onSelect: () => {
					if (filterState.issueIds.includes(issue.id)) {
						filterState.removeIssue(issue.id)
					} else {
						filterState.addIssue(issue.id)
					}
				},
				isSelected: filterState.issueIds.includes(issue.id),
				renderCustom: () => (
					<IssueStatusIcon
						color={issue.statusColor ?? ""}
						className="h-3.5 w-3.5 shrink-0"
						status={issue.status}
					/>
				),
			})
		})

		distinctIssueStatuses(issues).forEach((option) => {
			items.push({
				id: `issue-status-${option.name}`,
				category: "IssueStatuses",
				categoryLabel: "Issue status",
				label: option.name,
				searchText: `Issue status ${option.name}`,
				onSelect: () => {
					if (filterState.issueStatuses.includes(option.name)) {
						filterState.removeIssueStatus(option.name)
					} else {
						filterState.addIssueStatus(option.name)
					}
				},
				isSelected: filterState.issueStatuses.includes(option.name),
				renderCustom: () => (
					<IssueStatusIcon
						color={option.color}
						className="h-3.5 w-3.5 flex-none"
						status={option.status}
					/>
				),
			})
		})

		items.push({
			id: "issue-none",
			category: "Issues",
			categoryLabel: "Issue",
			label: "No issue linked",
			searchText: "Issue No issue linked",
			Icon: Link2Off,
			onSelect: () => {
				if (filterState.issueIds.includes(null)) {
					filterState.removeIssue(null)
				} else {
					filterState.addIssue(null)
				}
			},
			isSelected: filterState.issueIds.includes(null),
		})

		items.push({
			id: "company-none",
			category: "Companies",
			categoryLabel: "Companies",
			label: "No company linked",
			searchText: "Companies No company linked",
			Icon: Link2Off,
			onSelect: () => {
				if (filterState.companyIds.includes(null)) {
					filterState.removeCompany(null)
				} else {
					filterState.addCompany(null)
				}
			},
			isSelected: filterState.companyIds.includes(null),
		})

		companies.forEach((company) => {
			items.push({
				id: `company-${company.id}`,
				category: "Companies",
				categoryLabel: "Companies",
				label: company.name,
				secondaryLabel: companyDomain(company) ?? undefined,
				searchText: `Companies ${company.name} ${company.domains?.join(" ") ?? ""}`,
				onSelect: () => {
					if (filterState.companyIds.includes(company.id)) {
						filterState.removeCompany(company.id)
					} else {
						filterState.addCompany(company.id)
					}
				},
				isSelected: filterState.companyIds.includes(company.id),
				renderCustom: () => (
					<CompanyLogo
						linearLogoUrl={company?.logoUrl ?? undefined}
						brandfetchData={null}
						fallback={company.name}
						size="xs"
					/>
				),
			})
		})

		items.push({
			id: "person-none",
			category: "People",
			categoryLabel: "People",
			label: "No person linked",
			searchText: "People No person linked",
			Icon: Link2Off,
			onSelect: () => {
				if (filterState.personIds.includes(null)) {
					filterState.removePerson(null)
				} else {
					filterState.addPerson(null)
				}
			},
			isSelected: filterState.personIds.includes(null),
		})

		contacts.forEach((person) => {
			const name = person.name || person.email
			items.push({
				id: `person-${person.id}`,
				category: "People",
				categoryLabel: "People",
				label: name,
				searchText: `People ${person.name ?? ""} ${person.email}`,
				onSelect: () => {
					if (filterState.personIds.includes(person.id)) {
						filterState.removePerson(person.id)
					} else {
						filterState.addPerson(person.id)
					}
				},
				isSelected: filterState.personIds.includes(person.id),
				renderCustom: () => (
					<Avatar image={person.imageUrl} name={name} size="xs" />
				),
			})
		})

		return items
	}, [
		users,
		contacts,
		projects,
		tags,
		tagGroupColorMap,
		issues,
		companies,
		portalMarks,
		inboxAddresses,
		slackWorkspaces,
		filterState,
		onCategorySelect,
	])

	return allSearchableItems
}

export function useFilteredSearchItems(
	globalQuery: string,
	allSearchableItems: SearchableFilterItem[],
	filterState: {
		companyRevenue: NumericFilter | null
		setCompanyRevenue: (filter: NumericFilter | null) => void
		companySize: NumericFilter | null
		setCompanySize: (filter: NumericFilter | null) => void
	},
) {
	return useMemo(() => {
		if (!globalQuery) return []

		const nonCategoryItems = dedupeById(allSearchableItems).filter(
			(item) => !item.id.startsWith("category-"),
		)

		const fuse = new Fuse(nonCategoryItems, {
			keys: [
				{ name: "label", weight: 2 },
				{ name: "searchText", weight: 1 },
			],
			threshold: 0.4,
		})

		let results = fuse.search(globalQuery).map((result) => result.item)

		// When the query looks like a number, generate numeric filter items
		const numericValue = Number(globalQuery.trim())
		if (!isNaN(numericValue) && globalQuery.trim() !== "") {
			const numericItems: SearchableFilterItem[] = []
			for (const { operator, label } of NUMERIC_OPERATORS) {
				numericItems.push({
					id: `revenue-${operator}-${numericValue}`,
					category: "CompanyRevenue",
					categoryLabel: "Company revenue",
					label: `${label} $${numericValue}`,
					searchText: `Company revenue ${label} ${numericValue}`,
					onSelect: () => {
						filterState.setCompanyRevenue({
							operator,
							value: numericValue,
						})
					},
					isSelected:
						filterState.companyRevenue?.operator === operator &&
						filterState.companyRevenue?.value === numericValue,
				})
				numericItems.push({
					id: `size-${operator}-${numericValue}`,
					category: "CompanySize",
					categoryLabel: "Company size",
					label: `${label} ${numericValue}`,
					searchText: `Company size ${label} ${numericValue}`,
					onSelect: () => {
						filterState.setCompanySize({
							operator,
							value: numericValue,
						})
					},
					isSelected:
						filterState.companySize?.operator === operator &&
						filterState.companySize?.value === numericValue,
				})
			}
			results.push(...numericItems)
		}

		results = dedupeById(results)

		const categoryOrder: FilterType[] = [
			"Assignees",
			"Origins",
			"Portals",
			"Inboxes",
			"SlackWorkspaces",
			"SlackChannels",
			"Tags",
			"Priorities",
			"Statuses",
			"ReplyStates",
			"Companies",
			"People",
			"Projects",
			"Issues",
			"IssueStatuses",
			"Creators",
			"CompanyRevenue",
			"CompanyTiers",
			"CompanyStatuses",
			"CompanyOwners",
			"CompanySize",
		]

		const sortedResults = results.sort((a, b) => {
			const aIndex = categoryOrder.indexOf(a.category)
			const bIndex = categoryOrder.indexOf(b.category)
			return aIndex - bIndex
		})

		return sortedResults.slice(0, 15)
	}, [globalQuery, allSearchableItems, filterState])
}

type LucideIcon = FC<SVGProps<SVGSVGElement>>
